import { useMemo, useState } from 'react';
import type {
  Academy,
  AgeGroup,
  AcademySport,
  Coach,
  Lesson,
  Term,
  Location,
  PublicProgramSummary,
  ProRataPreviewResponse,
} from '@court-sync/shared';
import { Button } from '@/components/ui';
import { PublicLessonBrowser } from '@/components/public/PublicLessonBrowser';
import { OrgPrivateBookingEmbed } from '@/components/public/org/OrgPrivateBookingEmbed';
import { cn } from '@/lib/utils';

type LessonTab = 'group' | 'camps' | 'private';

export function resolveDefaultLessonTab(lessons: Lesson[]): LessonTab {
  const hasGroupLessons = lessons.some((lesson) => lesson.lessonType === 'group');
  const hasCampLessons = lessons.some((lesson) => lesson.lessonType === 'camp');
  if (!hasGroupLessons && hasCampLessons) {
    return 'camps';
  }
  return 'group';
}

interface OrgLessonsSectionProps {
  academy: Academy;
  academySlug: string;
  lessons: Lesson[];
  terms: Term[];
  academySports: AcademySport[];
  ageGroups: AgeGroup[];
  publicLocations: Location[];
  publicPrograms: PublicProgramSummary[];
  proRataPreviewByLessonId: Record<string, ProRataPreviewResponse | undefined>;
  resolvedManualInviteToken: string | null;
  buildOrgPath: (subpath: string) => string;
  onBookLesson: (lessonId: string) => void;
  onViewAll?: () => void;
  preview?: boolean;
}

export function OrgLessonsSection({
  academy,
  academySlug,
  lessons,
  terms,
  academySports,
  ageGroups,
  publicLocations,
  publicPrograms,
  proRataPreviewByLessonId,
  resolvedManualInviteToken,
  buildOrgPath,
  onBookLesson,
  onViewAll,
  preview = true,
}: OrgLessonsSectionProps) {
  const [tab, setTab] = useState<LessonTab>(() => resolveDefaultLessonTab(lessons));

  const tabs: { id: LessonTab; label: string }[] = [
    { id: 'group', label: 'Group' },
    { id: 'camps', label: 'Camps' },
    { id: 'private', label: 'Private' },
  ];

  const privateCatalogLessons = useMemo(
    () => lessons.filter((l) => l.lessonType === 'private'),
    [lessons]
  );

  const showPrivateBookingEmbed = academy.privateBookingEnabled === true;

  return (
    <section aria-labelledby="lessons-heading" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="lessons-heading" className="text-2xl font-bold text-foreground">
          Lessons & programs
        </h2>
        {onViewAll ? (
          <Button variant="outline" size="sm" onClick={onViewAll}>
            View all
          </Button>
        ) : null}
      </div>

      <div className="flex gap-1 rounded-lg border border-border bg-muted/30 p-1" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'private' ? (
        <div className="space-y-4">
          {showPrivateBookingEmbed ? (
            <OrgPrivateBookingEmbed
              academy={academy}
              academySlug={academySlug}
              buildOrgPath={buildOrgPath}
            />
          ) : null}
          {privateCatalogLessons.length > 0 ? (
            <PublicLessonBrowser
              academy={academy}
              academySlug={academySlug}
              lessons={privateCatalogLessons}
              terms={terms}
              academySports={academySports}
              ageGroups={ageGroups}
              publicLocations={publicLocations}
              publicPrograms={publicPrograms}
              proRataPreviewByLessonId={proRataPreviewByLessonId}
              resolvedManualInviteToken={resolvedManualInviteToken}
              onBookLesson={onBookLesson}
              buildAcademyPath={buildOrgPath}
              embeddedOnAcademyHome={preview}
              showLocationMap={false}
            />
          ) : !showPrivateBookingEmbed ? (
            <p className="text-sm text-muted-foreground">No private lessons available right now.</p>
          ) : null}
        </div>
      ) : (
        <PublicLessonBrowser
          academy={academy}
          academySlug={academySlug}
          lessons={lessons}
          paginatedScope={tab === 'camps' ? 'camp' : 'group'}
          terms={terms}
          academySports={academySports}
          ageGroups={ageGroups}
          publicLocations={publicLocations}
          publicPrograms={publicPrograms}
          proRataPreviewByLessonId={proRataPreviewByLessonId}
          resolvedManualInviteToken={resolvedManualInviteToken}
          onBookLesson={onBookLesson}
          buildAcademyPath={buildOrgPath}
          embeddedOnAcademyHome={preview}
          showLocationMap={false}
        />
      )}

    </section>
  );
}

interface OrgCoachesSectionProps {
  coaches: Coach[];
  showCoaches: boolean;
}

export function OrgCoachesSection({ coaches, showCoaches }: OrgCoachesSectionProps) {
  const activeCoaches = useMemo(() => coaches.filter((c) => c.isActive), [coaches]);

  if (!showCoaches || activeCoaches.length === 0) return null;

  return (
    <section aria-labelledby="coaches-heading" className="space-y-4">
      <h2 id="coaches-heading" className="text-2xl font-bold text-foreground">
        Our coaches
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {activeCoaches.map((coach) => (
          <article
            key={coach.id}
            className="rounded-lg border border-border bg-card p-4 text-center"
          >
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-lg font-bold text-muted-foreground">
              {coach.firstName?.[0]}
              {coach.lastName?.[0]}
            </div>
            <h3 className="font-semibold text-foreground">
              {coach.firstName} {coach.lastName}
            </h3>
            {coach.bio ? (
              <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{coach.bio}</p>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}
